import { useLang } from "@/i18n/LangContext";
import type { DeckCompatItem } from "@/types/api";
import { getLocaleString } from "@/lib/format";

interface GameStatsCardsProps {
  playerCount: number | null;
  steamSpyOwners: string;
  steamSpyAvgPlaytime: number;
  deckCompat: { category: number; items: DeckCompatItem[] } | null;
  tags: Record<string, number>;
}

export default function GameStatsCards({
  playerCount,
  steamSpyOwners,
  steamSpyAvgPlaytime,
  deckCompat,
  tags,
}: GameStatsCardsProps) {
  const { lang, t } = useLang();

  return (
    <>
      {/* 통계 카드 */}
      {(playerCount !== null || steamSpyOwners || steamSpyAvgPlaytime > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          {playerCount !== null && (
            <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-5">
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                  </svg>
                </div>
                <span className="text-xs font-semibold text-gray-400 dark:text-muted uppercase tracking-wider">{t.gameDetail.currentPlayers}</span>
              </div>
              <p className="text-2xl font-black text-gray-900 dark:text-white tabular-nums">
                {playerCount.toLocaleString(getLocaleString(lang))}
                <span className="text-sm font-medium text-gray-400 dark:text-muted ml-1">{t.gameDetail.playerUnit}</span>
              </p>
            </div>
          )}
          {steamSpyOwners && (
            <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-5">
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4-4v2" /><circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <span className="text-xs font-semibold text-gray-400 dark:text-muted uppercase tracking-wider">{t.gameDetail.estimatedOwners}</span>
              </div>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {steamSpyOwners}
              </p>
            </div>
          )}
          {steamSpyAvgPlaytime > 0 && (
            <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-5">
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <span className="text-xs font-semibold text-gray-400 dark:text-muted uppercase tracking-wider">{t.gameDetail.avgPlaytime}</span>
              </div>
              <p className="text-2xl font-black text-gray-900 dark:text-white tabular-nums">
                {Math.floor(steamSpyAvgPlaytime / 60)}
                <span className="text-sm font-medium text-gray-400 dark:text-muted ml-1">{t.gameDetail.hoursUnit}</span>
                {" "}{steamSpyAvgPlaytime % 60}
                <span className="text-sm font-medium text-gray-400 dark:text-muted ml-1">{t.gameDetail.minuteUnit}</span>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Steam Deck 호환성 */}
      {deckCompat && deckCompat.category > 0 && (
        <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <rect x="2" y="6" width="20" height="12" rx="3" />
                <circle cx="8" cy="12" r="2" />
                <circle cx="16" cy="12" r="2" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t.gameDetail.deckCompat}</h2>
              <span className="text-xs font-bold text-gray-600 dark:text-muted">
                {deckCompat.category === 3 ? t.gameDetail.deckVerified :
                 deckCompat.category === 2 ? t.gameDetail.deckPlayable :
                 t.gameDetail.deckUnsupported}
              </span>
            </div>
          </div>
          {deckCompat.items.length > 0 && (
            <div className="space-y-1.5">
              {deckCompat.items.map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <span className="shrink-0 mt-0.5 text-gray-900 dark:text-white">
                    {item.type === 4 ? "\u2713" : item.type === 3 ? "!" : "\u2717"}
                  </span>
                  <span className="text-gray-600 dark:text-muted">{item.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 인기 태그 */}
      {Object.keys(tags).length > 0 && (
        <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t.gameDetail.popularTags}</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(tags)
              .sort(([, a], [, b]) => b - a)
              .slice(0, 15)
              .map(([tag, count]) => (
                <span
                  key={tag}
                  className="text-xs bg-inner-bg dark:bg-inner-bg text-gray-700 dark:text-foreground px-3 py-1.5 rounded-lg font-medium border border-inner-border dark:border-card-border hover:bg-hover-bg dark:hover:bg-hover-bg transition-colors"
                  title={`${t.gameDetail.votesCount}: ${count}`}
                >
                  {tag}
                </span>
              ))}
          </div>
        </div>
      )}
    </>
  );
}
